/**
 * Pure helpers for the personal handbook document library (PROMANG_COMPONENTS.md §8.5):
 * read state per document, search/filter, «Må leses» list and flat chapter groups.
 */

export const LIBRARY_STATUS_FILTERS = ["all", "required", "done"];
export const NO_CHAPTER = "none";

export const isFillableForm = (doc) => {
  const template = doc?.fillable_template;
  return Boolean((template?.id ?? template?.template_id) && template?.active !== false);
};

/** form · exempt · done · optional · overdue · pending */
export function readState(doc) {
  if (isFillableForm(doc)) return "form";
  const status = doc?.current_user_read_status;
  if (status?.exempt_as_creator) return "exempt";
  if (status?.acknowledged_read_understood) return "done";
  if (status && status.read_required === false) return "optional";
  if (status?.requirement?.overdue) return "overdue";
  return "pending";
}

export const isMustRead = (doc) => ["pending", "overdue"].includes(readState(doc));

const text = (value) => String(value ?? "").toLowerCase();

export const chapterKey = (doc) => (doc?.chapter?.id != null ? String(doc.chapter.id) : NO_CHAPTER);

export const chapterLabel = (chapter) =>
  chapter ? [chapter.number, chapter.title].filter(Boolean).join(" ") : "";

/** Depth from the chapter number («4» → 0, «4.1» → 1), for indenting the table of contents. */
export const chapterDepth = (chapter) =>
  Math.max(0, String(chapter?.number || "").split(".").filter(Boolean).length - 1);

export function compareChapterNumbers(a, b) {
  const left = String(a || "").split(".").map((part) => parseInt(part, 10) || 0);
  const right = String(b || "").split(".").map((part) => parseInt(part, 10) || 0);
  for (let i = 0; i < Math.max(left.length, right.length); i += 1) {
    const diff = (left[i] || 0) - (right[i] || 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

const compareDocuments = (a, b) =>
  String(a.document_number || "").localeCompare(String(b.document_number || ""), "nb", { numeric: true }) ||
  String(a.title || "").localeCompare(String(b.title || ""), "nb", { numeric: true });

export function matchesQuery(doc, query) {
  const needle = text(query).trim();
  if (!needle) return true;
  return [
    doc.title,
    doc.document_number,
    doc.description,
    doc.document_type_display,
    doc.category?.name || doc.category_display,
    doc.discipline?.name,
    chapterLabel(doc.chapter),
  ].some((value) => text(value).includes(needle));
}

export function filterLibrary(documents = [], { query = "", status = "all", type = "", discipline = "", chapter = "" } = {}) {
  return documents.filter((doc) => {
    if (!matchesQuery(doc, query)) return false;
    if (status === "required" && !isMustRead(doc)) return false;
    if (status === "done" && readState(doc) !== "done") return false;
    if (type && (doc.document_type_display || "") !== type) return false;
    if (discipline && String(doc.discipline?.id ?? "") !== String(discipline)) return false;
    if (chapter && chapterKey(doc) !== chapter) return false;
    return true;
  });
}

const dueTime = (doc) => {
  const value = doc?.current_user_read_status?.requirement?.due_at;
  const time = value ? new Date(value).getTime() : NaN;
  return Number.isNaN(time) ? Number.POSITIVE_INFINITY : time;
};

/** Required, unconfirmed documents: overdue first, then the nearest deadline. */
export const mustReadList = (documents = []) =>
  documents
    .filter(isMustRead)
    .sort((a, b) => (readState(b) === "overdue") - (readState(a) === "overdue") || dueTime(a) - dueTime(b) || compareDocuments(a, b));

/** Flat chapter groups in chapter-number order; documents without a chapter last. */
export function groupByChapter(documents = []) {
  const groups = new Map();
  documents.forEach((doc) => {
    const key = chapterKey(doc);
    if (!groups.has(key)) groups.set(key, { key, chapter: doc.chapter || null, documents: [] });
    groups.get(key).documents.push(doc);
  });
  return [...groups.values()]
    .map((group) => ({ ...group, documents: [...group.documents].sort(compareDocuments) }))
    .sort((a, b) => {
      if (a.key === NO_CHAPTER) return 1;
      if (b.key === NO_CHAPTER) return -1;
      return (
        compareChapterNumbers(a.chapter?.number, b.chapter?.number) ||
        String(a.chapter?.title || "").localeCompare(String(b.chapter?.title || ""), "nb")
      );
    });
}

/** Facet options with counts, e.g. [{ value: "Prosedyre", label: "Prosedyre", count: 12 }]. */
export function facetOptions(documents = [], pick) {
  const counts = new Map();
  documents.forEach((doc) => {
    const option = pick(doc);
    if (!option?.value) return;
    const current = counts.get(option.value) || { ...option, count: 0 };
    current.count += 1;
    counts.set(option.value, current);
  });
  return [...counts.values()].sort((a, b) => String(a.label).localeCompare(String(b.label), "nb"));
}
